/**
 * iOS keyboard viewport-pan restorer.
 *
 * The document never scrolls in this app (#root is the scroll container), so
 * the window's scroll offset should always be 0. iOS ignores that when the
 * software keyboard opens — it pans the whole web view up so the focused
 * input stays visible — and, especially in installed (standalone) PWAs, it
 * sometimes fails to pan back after the keyboard dismisses. Everything
 * `position: fixed` (top nav, bottom tab bar) is left offset upward with a
 * dead band under the page: the "tab bar floats above the bottom / login
 * looks scrolled" bug.
 *
 * Whenever no editable element has focus, any nonzero window scroll is a
 * stuck pan — snap it back. Checked after focus leaves an input, when the
 * visual viewport resizes (keyboard closing), and when the app resumes.
 */
export function initViewportPanFix(): void {
  const editableFocused = () => {
    const el = document.activeElement;
    return (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      (el instanceof HTMLElement && el.isContentEditable)
    );
  };

  const restore = () => {
    if (editableFocused()) return;
    if (window.scrollY !== 0 || window.scrollX !== 0) {
      window.scrollTo(0, 0);
    }
    // The visual viewport can also stay panned independently of scrollY.
    const vv = window.visualViewport;
    if (vv && (vv.offsetTop > 0 || vv.pageTop > 0) && !editableFocused()) {
      window.scrollTo(0, 0);
    }
  };
  // Keyboard dismissal settles over a few frames — check a couple of times.
  const restoreSoon = () => {
    setTimeout(restore, 60);
    setTimeout(restore, 300);
  };

  window.addEventListener("focusout", restoreSoon);
  window.visualViewport?.addEventListener("resize", restoreSoon);
  document.addEventListener("visibilitychange", restoreSoon);
  window.addEventListener("orientationchange", restoreSoon);
  // Any window-level scroll while NOT typing is illegitimate (the document
  // never scrolls by design) — snap it back as soon as it settles.
  window.addEventListener("scroll", restoreSoon, { passive: true });

  initViewportDebugOverlay();
}

/**
 * Field diagnostic: shows live viewport numbers (works inside the installed
 * PWA, where devtools are unreachable). Toggled by five quick taps anywhere
 * in the top-left quarter of the header, and — while the layout bug hunt is
 * on — appears automatically in standalone mode when the geometry looks
 * wrong (viewport shorter than the screen). Tap the panel itself to dismiss.
 */
function initViewportDebugOverlay(): void {
  let taps: number[] = [];
  let box: HTMLDivElement | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  const readSafeBottom = () => {
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;bottom:0;height:env(safe-area-inset-bottom,0px);width:1px;visibility:hidden;pointer-events:none";
    document.body.appendChild(probe);
    const px = probe.getBoundingClientRect().height;
    probe.remove();
    return px;
  };
  const readDvh = () => {
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;top:0;height:100dvh;width:1px;visibility:hidden;pointer-events:none";
    document.body.appendChild(probe);
    const px = probe.getBoundingClientRect().height;
    probe.remove();
    return px;
  };

  const update = () => {
    if (!box) return;
    const vv = window.visualViewport;
    const nav = document.querySelector('nav[aria-label="Primary"]');
    const navBottom = nav ? Math.round(nav.getBoundingClientRect().bottom) : -1;
    box.textContent = [
      `build ${(import.meta.env.VITE_APP_VERSION as string | undefined)?.slice(0, 7) || "dev"}`,
      `innerH ${window.innerHeight}  screenH ${window.screen.height}`,
      `vvH ${vv ? Math.round(vv.height) : "-"}  vvTop ${vv ? Math.round(vv.offsetTop) : "-"}  pageTop ${vv ? Math.round(vv.pageTop) : "-"}`,
      `dvh ${Math.round(readDvh())}  safeBottom ${Math.round(readSafeBottom())}`,
      `scrollY ${Math.round(window.scrollY)}  barBottom ${navBottom}`,
      `standalone ${(navigator as { standalone?: boolean }).standalone ?? "?"}  dpr ${window.devicePixelRatio}`,
    ].join("\n");
  };

  const show = () => {
    if (box) return;
    box = document.createElement("div");
    box.style.cssText =
      "position:fixed;top:80px;left:8px;z-index:99999;background:rgba(0,0,0,0.82);color:#7CFC9A;" +
      "font:11px/1.5 monospace;padding:8px 10px;border-radius:8px;white-space:pre;cursor:pointer";
    box.addEventListener("click", hide);
    document.body.appendChild(box);
    update();
    timer = setInterval(update, 500);
  };
  const hide = () => {
    box?.remove();
    box = null;
    if (timer) clearInterval(timer);
    timer = null;
  };

  document.addEventListener("click", (e) => {
    // Generous zone: the whole top-left header area, so it still works when
    // the page is misaligned with the physical screen.
    if (e.clientY > 200 || e.clientX > 200) return;
    const now = Date.now();
    taps = [...taps.filter((t) => now - t < 2500), now];
    if (taps.length < 5) return;
    taps = [];
    if (box) hide();
    else show();
  });

  // NOTE: a short layout viewport in standalone mode is usually the iOS 26.1
  // system bug (PWAs letterboxed in portrait; fixed by Apple in iOS 26.2) —
  // nothing the page can work around, so don't auto-surface the panel for
  // end users. The 5-tap gesture above remains for field diagnosis.
}
